/**
 * Staff traffic: teletype dispatches, telegrams, the MET wording and the rules the weather
 * changes. Parameters are prepared by game/format.ts in the target language (`unit`, `at`), or
 * passed raw where grammar depends on them (a nation's adjective, a place, a unit type).
 */
import type { Posture, UnitType } from '@friction/engine';
import { type NationRef, type Place, placeLabel, placePhraseOf } from '../names';

/** An order's execution time: clock ("05:00") and day. */
export interface HHour {
  hour: string;
  day: number;
}

const adjOr = (adj: string | null): string => adj ?? 'enemy';
const nameOr = (n: NationRef | null): string => n?.name ?? 'the enemy';
const typeWord: Record<UnitType, string> = {
  infantry: 'infantry',
  motorized: 'motorized',
  armor: 'armor',
  artillery: 'artillery',
  recon: 'reconnaissance',
  hq: 'headquarters',
  decoy: 'decoy',
};

export const dispatch = {
  // ---- teletype heads (stencilled before the text)
  'tty.head.contact': 'CONTACT',
  'tty.head.assault': 'ASSAULT',
  'tty.head.underAttack': 'UNDER ATTACK',
  'tty.head.success': 'SUCCESS',
  'tty.head.repulsed': 'REPULSED',
  'tty.head.breakthrough': 'BREAKTHROUGH',
  'tty.head.held': 'HELD',
  'tty.head.withdrawal': 'WITHDRAWAL',
  'tty.head.pursuit': 'PURSUIT',
  'tty.head.surrender': 'SURRENDER',
  'tty.head.attrition': 'ATTRITION',
  'tty.head.loss': 'LOSS',
  'tty.head.prisoners': 'PRISONERS',
  'tty.head.destroyed': 'DESTROYED',
  'tty.head.captured': 'CAPTURED',
  'tty.head.lost': 'LOST',
  'tty.head.kessel': 'KESSEL',
  'tty.head.signal': 'SIGNAL',
  'tty.head.courierMissing': 'COURIER MISSING',
  'tty.head.intercepted': 'INTERCEPTED',
  'tty.head.decrypted': 'DECRYPTED',
  'tty.head.mobilised': 'MOBILISED',
  'tty.head.research': 'RESEARCH',
  'tty.head.deception': 'DECEPTION',
  'tty.head.decoy': 'DECOY',
  'tty.head.hqLost': 'HQ LOST',
  'tty.head.hqDestroyed': 'HQ DESTROYED',
  'tty.head.capitulation': 'CAPITULATION',
  'tty.head.armistice': 'ARMISTICE',
  'tty.head.generalStaff': 'GENERAL STAFF',
  'tty.head.met': 'MET',

  // ---- teletype texts
  'tty.contact': '{unit} reports {what} {at}',
  'tty.contact.ourPatrol': 'our patrol',
  'tty.what': (p: { type: UnitType | null }) => (p.type ? `enemy ${typeWord[p.type]}` : 'enemy contact'),
  'tty.assault': 'our attack {at} has begun',
  'tty.underAttack': (p: { adj: string | null; at: string }) =>
    `${adjOr(p.adj)} forces assaulting our positions ${p.at}`,
  'tty.fighting': 'fighting observed {at}',
  'tty.success': 'position {at} carried',
  'tty.repulsed': 'our attack {at} has been thrown back',
  'tty.breakthrough': 'our line {at} is broken',
  'tty.held': 'enemy attack {at} beaten off',
  'tty.actionCarried': 'action {at} concluded — attackers carried the position',
  'tty.actionHeld': 'action {at} concluded — defence held',
  'tty.withdrawal': (p: { unit: string; from: Place }) =>
    `${p.unit} falling back from ${placeLabel(p.from, 'en')}`,
  'tty.pursuit': 'enemy withdrawing {at}',
  'tty.surrender': '{unit} has laid down its arms {at}',
  'tty.attrition': '{unit} dissolved for want of supply',
  'tty.loss': '{unit} destroyed {at}',
  'tty.enemyLoss': (p: {
    unit: string;
    adj: string | null;
    cause: 'combat' | 'surrender' | 'attrition';
    at: string;
  }) => {
    const adj = adjOr(p.adj);
    // Designations sometimes carry the nationality already ("6th Skaudavan Infantry Division").
    const who = p.unit.toLowerCase().includes(adj.toLowerCase()) ? `enemy ${p.unit}` : `${adj} ${p.unit}`;
    const how = p.cause === 'surrender' ? 'surrendered' : p.cause === 'attrition' ? 'dissolved' : 'destroyed';
    return `${who} ${how} ${p.at}`;
  },
  'tty.captured': (p: { city: string; adj: string | null }) => `${p.city} taken from the ${adjOr(p.adj)}`,
  'tty.lost': (p: { city: string; adj: string | null }) => `${p.city} has fallen to ${adjOr(p.adj)} forces`,
  'tty.fallsTo': (p: { city: string; to: NationRef | null }) => `${p.city} falls to ${nameOr(p.to)}`,
  'tty.kesselOurs': (p: { n: number; at: string }) =>
    `${p.n} ${p.n === 1 ? 'division' : 'divisions'} of ours encircled ${p.at}`,
  'tty.kesselTheirs': (p: { n: number; adj: string | null; at: string }) =>
    `${p.n} ${p.n === 1 ? 'division' : 'divisions'} ${adjOr(p.adj)} encircled ${p.at}`,
  'tty.acknowledged': '{unit} acknowledges orders ({h}h in transit)',
  'tty.executing': '{unit} carrying out orders',
  'tty.courierLost': 'rider to {unit} overdue {at} — orders may be compromised',
  'tty.intercept': (p: {
    method: 'captured-courier' | 'decrypted-radio';
    unit: string;
    order: string;
    hhour: HHour | null;
  }) =>
    `${p.method === 'captured-courier' ? 'enemy courier captured' : 'enemy radio traffic decrypted'}: orders for ${p.unit} to ${p.order}${p.hhour ? ` at H ${p.hhour.hour} D${p.hhour.day}` : ''}`,
  'tty.raised': (p: { type: UnitType; city: string }) =>
    `new ${typeWord[p.type]} formation raised at ${p.city}`,
  'tty.researched': '{tech} completed',
  'tty.decoyOurs': 'our dummy formation {at} has been exposed',
  'tty.decoyTheirs': 'contact {at} unmasked as a dummy formation',
  'tty.hqLost': '{unit} overrun — orders will be delayed',
  'tty.hqDestroyed': (p: { adj: string | null }) => `${adjOr(p.adj)} headquarters eliminated`,
  'tty.capitulatedUs': 'the government has capitulated',
  'tty.capitulated': (p: { nation: NationRef | null; by: NationRef | null }) =>
    `${nameOr(p.nation)} has capitulated to ${nameOr(p.by)}`,
  'tty.armistice': 'hostilities have ceased',
  'tty.war': (p: { theatre: string }) => `hostilities commence across ${p.theatre}. All formations stand to`,
  'tty.forecast': (p: { what: string; region: Place; when: string; chance: number }) =>
    `${p.what} expected over ${placeLabel(p.region, 'en')} ${p.when} (${p.chance} %)`,
  'tty.forecast.dawn': 'at dawn',
  'tty.forecast.by': 'by {clock}',

  // ---- order phrases (intercepts, telegrams)
  'orderPhrase.move': (p: { place: Place }) => `move ${placePhraseOf(p.place, 'en')}`,
  'orderPhrase.attack': (p: { place: Place }) => `attack ${placeLabel(p.place, 'en')}`,
  'orderPhrase.hold': (p: { place: Place }) => `hold ${placePhraseOf(p.place, 'en')}`,
  'orderPhrase.retreat': (p: { place: Place }) => `fall back ${placePhraseOf(p.place, 'en')}`,
  'orderPhrase.posture': (p: { posture: Posture | null }) => `adopt a ${p.posture ?? 'new'} posture`,

  // ---- weather: forecast conditions, ground notices, telegrams, rules in force
  'wx.cond.rain': 'rain',
  'wx.cond.storm': 'heavy storms',
  'wx.cond.snow': 'snowfall',
  'wx.cond.blizzard': 'blizzards',
  'wx.cond.fog': 'dense morning fog',
  'wx.change.frost.head': 'HARD FROST',
  'wx.change.frost': 'the ground has frozen across the theatre — rivers and marshes bear, the mud is gone',
  'wx.change.thaw.head': 'THAW',
  'wx.change.thaw': 'the ice is breaking on the rivers — expect the roads to soften',
  'wx.change.mud.head': 'RASPUTITSA',
  'wx.change.mud': 'the fields have turned to mud — armour and lorries are bound to the roads',
  'wx.change.firm.head': 'GROUND FIRM',
  'wx.change.firm': 'the fields are drying out — vehicles can leave the roads again',
  'wx.change.snow.head': 'SNOW',
  'wx.change.snow': 'snow lies across the theatre — slow going off the roads',
  'wx.fx.marching': 'marching {pct}',
  'wx.fx.attacks': 'attacks {pct}',
  'wx.fx.sight': 'sight −{n}',
  'wx.fx.hidden': 'units hidden beyond 1 hex',
  'wx.fx.attackers': 'attackers {pct}',
  'wx.fx.noAir': 'no air photos',
  'wx.fx.mud': 'off-road: foot {foot}, vehicles {motor}',
  'wx.fx.tankAttacks': 'tank attacks {pct}',
  'wx.fx.snow': 'off-road {pct}',
  'wx.fx.frozenMarsh': 'frozen marsh bears like plains',
  'wx.fx.frozenRivers': 'rivers frozen: no bridge needed',

  // ---- telegrams
  'tg.cityCaptured.title': 'City captured',
  'tg.cityCaptured': (p: { city: string; adj: string | null; size: 1 | 2 | 3 }) =>
    `${p.city} is in our hands. The ${adjOr(p.adj)} garrison has been expelled from the ${['', 'town', 'city', 'metropolis'][p.size]}.`,
  'tg.cityCaptured.stamp': 'Captured',
  'tg.cityLost.title': 'City lost',
  'tg.cityLost': (p: { city: string; adj: string | null }) =>
    `${p.city} has fallen to ${adjOr(p.adj)} forces. Its industry is lost to us.`,
  'tg.cityLost.stamp': 'Lost',
  'tg.kesselOurs.title': 'Encirclement',
  'tg.kesselOurs': {
    one: '{n} of our divisions is cut off {at}. Supply has ceased. Break out or relieve it.',
    other: '{n} of our divisions are cut off {at}. Supply has ceased. Break out or relieve them.',
  },
  'tg.kesselTheirs.title': 'Pocket closed',
  'tg.kesselTheirs': (p: { n: number; adj: string | null; at: string }) =>
    `${p.n} ${adjOr(p.adj)} ${p.n === 1 ? 'division is' : 'divisions are'} encircled ${p.at}. Hold the ring and they will starve.`,
  'tg.kessel.stamp': 'Kessel',
  'tg.courierCaptured.title': 'Courier captured',
  'tg.radioDecrypt.title': 'Radio decrypt',
  'tg.intercept': (p: { unit: string; order: string; hhour: HHour | null }) =>
    `Orders for ${p.unit}: ${p.order}.${p.hhour ? ` H-hour H ${p.hhour.hour} D${p.hhour.day}.` : ''}`,
  'tg.intercepted.stamp': 'Intercepted',
  'tg.decrypted.stamp': 'Decrypted',
  'tg.courierLost.title': 'Courier missing',
  'tg.courierLost': 'The rider carrying orders to {unit} never arrived. Assume the enemy has read them.',
  'tg.courierLost.stamp': 'Compromised',
  'tg.research.title': 'Research complete',
  'tg.research': '{name}: {desc}',
  'tg.research.stamp': 'Approved',
  'tg.hqLost.title': 'Headquarters lost',
  'tg.hqLost': (p: { unit: string | null }) =>
    `${p.unit ?? 'A headquarters'} has been overrun. Orders to its sector will be delayed.`,
  'tg.hqLost.stamp': 'HQ lost',
  'tg.war.title': 'Mobilisation',
  'tg.war': (p: { theatre: string }) =>
    `War has broken out across ${p.theatre}. Orders leave headquarters by telephone and courier — guard your couriers, keep your headquarters close to the front, and watch your flanks.`,
  'tg.war.stamp': 'Mobilise',
  'tg.mud.title': 'Mud season',
  'tg.mud':
    'Rain has soaked the country. Off the roads tanks and lorries crawl at half pace and attack poorly; supply columns bog down. Hold the roads.',
  'tg.frost.title': 'The frost has come',
  'tg.frost':
    'The ground has frozen hard. Rivers and marshes bear men and vehicles; the mud is gone. Winter warfare begins.',
  'tg.thaw.title': 'Thaw',
  'tg.thaw':
    'The frost is breaking. River ice is going out and the ground is softening — expect mud off the roads.',
  'tg.met.stamp': 'Met Service',
  'tg.capitulation.title': 'Capitulation',
  'tg.capitulationUs': 'The government has accepted terms. Our forces are ordered to lay down their arms.',
  'tg.capitulation': (p: { nation: NationRef | null; by: NationRef | null }) =>
    `${nameOr(p.nation)} has capitulated to ${nameOr(p.by)}. Its territory passes to the occupiers.`,
  'tg.capitulation.stamp': 'Capitulated',
} as const;
